import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Plus, Target, Trash2, Edit2, AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, ArrowRight, PiggyBank, Gift, History, Table2, Download, Info, TrendingUp, TrendingDown } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

export default function BudgetGoals() {
  const [goals, setGoals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  // Form state
  const [formCategoryId, setFormCategoryId] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formPeriodType, setFormPeriodType] = useState("annual");
  const [formNotes, setFormNotes] = useState("");
  const [formEffectiveDate, setFormEffectiveDate] = useState("");
  const [signWarning, setSignWarning] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({ NotTracked: true }); // NotTracked defaults to collapsed
  
  // Zero-sum reallocation state
  const [showReallocation, setShowReallocation] = useState(false);
  const [reallocationSource, setReallocationSource] = useState("");
  const [reallocationNote, setReallocationNote] = useState("");
  const [budgetChange, setBudgetChange] = useState(null); // { direction: 'increase'|'decrease', amount: number }
  const [budgetChanges, setBudgetChanges] = useState([]); // Change history
  const [showHistory, setShowHistory] = useState(false);
  const [spendingData, setSpendingData] = useState([]); // Spending data for calculating available budget
  const [retirementSummary, setRetirementSummary] = useState(null); // Retirement contribution data

  // Batch goal setting state
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchShowAll, setBatchShowAll] = useState(false);
  const [batchGoals, setBatchGoals] = useState({}); // { categoryId: { amount: string, periodType: string, originalGoalId: string | null } }
  const [batchSaving, setBatchSaving] = useState(false);

  // YoY comparison state
  const [showYoYComparison, setShowYoYComparison] = useState(false);
  const [previousYearGoals, setPreviousYearGoals] = useState([]);
  const [previousYearSpending, setPreviousYearSpending] = useState([]);

  // % of income calculator state
  const [showPercentCalc, setShowPercentCalc] = useState(false);
  const [percentOfIncome, setPercentOfIncome] = useState("");

  // Helper to calculate YTD spending for a category
  const getYtdSpent = (categoryId) => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const categorySpending = spendingData.filter(
      s => s.category_id === categoryId && s.month <= currentMonth
    );
    return categorySpending.reduce((sum, s) => sum + Math.abs(s.amount), 0);
  };

  // Helper to calculate available budget (budget - spent)
  const getAvailableBudget = (categoryId) => {
    const goal = goals.find(g => g.category_id === categoryId);
    if (!goal) return 0;
    const ytdSpent = getYtdSpent(categoryId);
    return Math.max(0, goal.annual_amount - ytdSpent);
  };

  // Set default effective date when year changes
  useEffect(() => {
    setFormEffectiveDate(`${year}-01-01`);
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [year]);

  // Fetch previous year data when YoY comparison is toggled on
  useEffect(() => {
    if (showYoYComparison) {
      fetchPreviousYearData();
    }
  }, [showYoYComparison, year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [goalsRes, catsRes, changesRes, spendingRes, retirementRes] = await Promise.all([
        api.get(`/budget-goals?year=${year}`),
        api.get("/categories"),
        api.get(`/budget-changes?year=${year}`),
        api.get(`/spending?year=${year}`),
        api.get(`/retirement-contributions?year=${year}`),
      ]);
      setGoals(goalsRes.data);
      setCategories(catsRes.data);
      setBudgetChanges(changesRes.data);
      setSpendingData(spendingRes.data);
      setRetirementSummary(retirementRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousYearData = async () => {
    const prevYear = year - 1;
    try {
      const [goalsRes, spendingRes] = await Promise.all([
        api.get(`/budget-goals?year=${prevYear}`),
        api.get(`/spending?year=${prevYear}`),
      ]);
      setPreviousYearGoals(goalsRes.data);
      setPreviousYearSpending(spendingRes.data);
    } catch (error) {
      console.error("Failed to load previous year data");
    }
  };

  // Get previous year goal for a category
  const getPreviousYearGoal = (categoryId) => {
    return previousYearGoals.find(g => g.category_id === categoryId);
  };

  // Get previous year actual spending for a category
  const getPreviousYearActual = (categoryId) => {
    const categorySpending = previousYearSpending.filter(s => s.category_id === categoryId);
    return categorySpending.reduce((sum, s) => sum + Math.abs(s.amount), 0);
  };

  // Export goals to CSV
  const exportToCSV = () => {
    const headers = ["Category", "Group", "Amount", "Period", "Annual Amount", "Notes"];
    const rows = goals.map(goal => {
      const cat = getCategoryById(goal.category_id);
      return [
        cat?.name || "Unknown",
        cat?.group || "Unknown",
        goal.amount,
        goal.period_type,
        goal.annual_amount,
        goal.notes || ""
      ];
    });
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `budget_goals_${year}.csv`;
    link.click();
    toast.success("Goals exported to CSV");
  };

  // Calculate amount from % of take-home income
  const calculateFromPercent = () => {
    const incomeTotal = getGroupTotal("Income");
    if (!incomeTotal || !percentOfIncome) return;
    
    const percent = parseFloat(percentOfIncome);
    if (isNaN(percent) || percent < 0 || percent > 100) {
      toast.error("Please enter a valid percentage (0-100)");
      return;
    }
    
    const calculatedAmount = (incomeTotal * percent / 100);
    // Calculate based on period type
    let finalAmount;
    if (formPeriodType === "weekly") {
      finalAmount = calculatedAmount / 52;
    } else if (formPeriodType === "monthly") {
      finalAmount = calculatedAmount / 12;
    } else {
      finalAmount = calculatedAmount;
    }
    
    setFormAmount(finalAmount.toFixed(2));
    setShowPercentCalc(false);
    setPercentOfIncome("");
    toast.success(`Set to ${percent}% of ${formatCurrency(incomeTotal)} income`);
  };

  const getSelectedCategory = () => categories.find((c) => c.id === formCategoryId);

  const validateSign = () => {
    const amount = parseFloat(formAmount);
    const category = getSelectedCategory();
    if (!category || isNaN(amount)) return null;

    const isIncome = category.group === "Income";
    const isNegative = amount < 0;

    if (isIncome && isNegative) {
      return {
        type: "income_negative",
        message: `You entered a negative amount for an Income category. Income budgets should be positive (expected earnings). Did you mean $${Math.abs(amount).toLocaleString()}?`
      };
    } else if (!isIncome && isNegative) {
      return {
        type: "expense_negative",
        message: `You entered a negative amount for an expense category. Expense budgets should be positive (spending limit). Did you mean $${Math.abs(amount).toLocaleString()}?`
      };
    }
    return null;
  };

  // Calculate if this is an increase or decrease when editing, with prorated "rest of year" impact
  const calculateBudgetChange = () => {
    if (!editingGoal || !formAmount || !formPeriodType) return null;
    
    const newAmount = Math.abs(parseFloat(formAmount));
    if (isNaN(newAmount)) return null;
    
    // Normalize both to annual for comparison
    const normalizeToAnnual = (amt, period) => {
      if (period === "weekly") return amt * 52;
      if (period === "monthly") return amt * 12;
      return amt;
    };
    
    const oldAnnual = normalizeToAnnual(editingGoal.amount, editingGoal.period_type);
    const newAnnual = normalizeToAnnual(newAmount, formPeriodType);
    const fullYearDiff = newAnnual - oldAnnual;
    
    if (Math.abs(fullYearDiff) < 1) return null; // No significant change
    
    // Calculate prorated impact based on effective date
    const effectiveDate = formEffectiveDate ? new Date(formEffectiveDate) : new Date();
    const endOfYear = new Date(year, 11, 31);
    const startOfYear = new Date(year, 0, 1);
    const totalDays = (endOfYear - startOfYear) / (1000 * 60 * 60 * 24) + 1;
    const remainingDays = Math.max(0, (endOfYear - effectiveDate) / (1000 * 60 * 60 * 24) + 1);
    const proratedFactor = remainingDays / totalDays;
    const proratedDiff = fullYearDiff * proratedFactor;
    
    return {
      direction: fullYearDiff > 0 ? "increase" : "decrease",
      amount: Math.abs(fullYearDiff), // Full year impact
      proratedAmount: Math.abs(proratedDiff), // Rest of year impact
      proratedFactor,
      remainingDays: Math.round(remainingDays),
      oldAnnual,
      newAnnual
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formCategoryId || !formAmount) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check for sign issues
    const warning = validateSign();
    if (warning && !pendingSubmit) {
      setSignWarning(warning);
      return;
    }

    // Check if this is an edit with a budget change that needs reallocation
    const change = calculateBudgetChange();
    if (change && editingGoal && !showReallocation && !reallocationSource) {
      setBudgetChange(change);
      setShowReallocation(true);
      return;
    }

    try {
      // Use absolute value for the amount (budgets are always stored positive)
      const amount = Math.abs(parseFloat(formAmount));
      
      await api.post("/budget-goals", {
        category_id: formCategoryId,
        year,
        amount: amount,
        period_type: formPeriodType,
        notes: formNotes || null,
        effective_date: formEffectiveDate || `${year}-01-01`,
        reallocation_source: reallocationSource || null,
        reallocation_note: reallocationNote || null,
      });
      
      toast.success(editingGoal ? "Goal updated!" : "Goal created!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save goal");
    }
  };

  const submitWithReallocation = async (source) => {
    setReallocationSource(source);
    // Small delay to let state update, then submit
    setTimeout(async () => {
      try {
        const amount = Math.abs(parseFloat(formAmount));
        
        await api.post("/budget-goals", {
          category_id: formCategoryId,
          year,
          amount: amount,
          period_type: formPeriodType,
          notes: formNotes || null,
          effective_date: formEffectiveDate || `${year}-01-01`,
          reallocation_source: source,
          reallocation_note: reallocationNote || null,
        });
        
        toast.success("Budget updated and reallocated!");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error("Failed to save goal");
      }
    }, 50);
  };

  const confirmWithWarning = async () => {
    setPendingSubmit(true);
    setSignWarning(null);
    // Re-trigger submit
    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent);
    setPendingSubmit(false);
  };

  const fixSignAndSubmit = async () => {
    setFormAmount(String(Math.abs(parseFloat(formAmount))));
    setSignWarning(null);
    setPendingSubmit(true);
    // Small delay to let state update
    setTimeout(async () => {
      const fakeEvent = { preventDefault: () => {} };
      await handleSubmit(fakeEvent);
      setPendingSubmit(false);
    }, 50);
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm("Delete this budget goal?")) return;
    
    try {
      await api.delete(`/budget-goals/${goalId}`);
      toast.success("Goal deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormCategoryId(goal.category_id);
    setFormAmount(String(goal.amount));
    setFormPeriodType(goal.period_type);
    setFormNotes(goal.notes || "");
    // When EDITING, default to today's date (not Jan 1)
    const today = new Date().toISOString().split('T')[0];
    setFormEffectiveDate(today);
    setShowReallocation(false);
    setReallocationSource("");
    setReallocationNote("");
    setBudgetChange(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingGoal(null);
    setFormCategoryId("");
    setFormAmount("");
    setFormPeriodType("annual");
    setFormNotes("");
    // When CREATING, default to Jan 1 of year
    setFormEffectiveDate(`${year}-01-01`);
    setSignWarning(null);
    setPendingSubmit(false);
    setShowReallocation(false);
    setReallocationSource("");
    setReallocationNote("");
    setBudgetChange(null);
  };

  // Batch goal functions
  const initializeBatchGoals = () => {
    const initialBatch = {};
    categories.forEach(cat => {
      const existingGoal = goals.find(g => g.category_id === cat.id);
      if (existingGoal) {
        initialBatch[cat.id] = {
          amount: String(existingGoal.amount),
          periodType: existingGoal.period_type,
          originalGoalId: existingGoal.id,
          originalAmount: existingGoal.amount,
          originalPeriodType: existingGoal.period_type
        };
      } else {
        initialBatch[cat.id] = {
          amount: "",
          periodType: "annual",
          originalGoalId: null,
          originalAmount: null,
          originalPeriodType: null
        };
      }
    });
    setBatchGoals(initialBatch);
  };

  const handleOpenBatchDialog = () => {
    initializeBatchGoals();
    setBatchDialogOpen(true);
  };

  const updateBatchGoal = (categoryId, field, value) => {
    setBatchGoals(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [field]: value
      }
    }));
  };

  const handleBatchSave = async () => {
    setBatchSaving(true);
    let created = 0;
    let updated = 0;
    let deleted = 0;
    let errors = 0;

    try {
      for (const [categoryId, goalData] of Object.entries(batchGoals)) {
        const { amount, periodType, originalGoalId, originalAmount, originalPeriodType } = goalData;
        const trimmedAmount = amount.trim();
        
        // Case 1: Had a goal, now cleared -> Delete
        if (originalGoalId && trimmedAmount === "") {
          try {
            await api.delete(`/budget-goals/${originalGoalId}`);
            deleted++;
          } catch (err) {
            errors++;
          }
          continue;
        }
        
        // Case 2: Empty field with no original goal -> Skip
        if (!originalGoalId && trimmedAmount === "") {
          continue;
        }
        
        // Case 3: Has a value -> Create or Update
        if (trimmedAmount !== "") {
          const numAmount = parseFloat(trimmedAmount);
          if (isNaN(numAmount)) {
            errors++;
            continue;
          }
          
          // Check if anything actually changed
          if (originalGoalId && numAmount === originalAmount && periodType === originalPeriodType) {
            continue; // No change, skip
          }
          
          try {
            await api.post("/budget-goals", {
              category_id: categoryId,
              year,
              amount: Math.abs(numAmount),
              period_type: periodType,
              notes: null,
              effective_date: `${year}-01-01`,
              reallocation_source: null,
              reallocation_note: null,
            });
            if (originalGoalId) {
              updated++;
            } else {
              created++;
            }
          } catch (err) {
            errors++;
          }
        }
      }

      if (errors > 0) {
        toast.warning(`Saved with ${errors} error(s): ${created} created, ${updated} updated, ${deleted} deleted`);
      } else if (created + updated + deleted > 0) {
        toast.success(`Goals saved: ${created} created, ${updated} updated, ${deleted} deleted`);
      } else {
        toast.info("No changes to save");
      }
      
      setBatchDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save batch goals");
    } finally {
      setBatchSaving(false);
    }
  };

  // Get categories to show in batch dialog based on toggle, sorted by group and sort_order
  const getBatchCategories = () => {
    const groupOrder = { "Income": 0, "Bills": 1, "Needs": 2, "Wants": 3 };
    const sortFn = (a, b) => {
      const groupDiff = (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99);
      if (groupDiff !== 0) return groupDiff;
      return (a.sort_order || 0) - (b.sort_order || 0);
    };
    
    if (batchShowAll) {
      return [...categories].sort(sortFn);
    }
    return [...categoriesWithoutGoals].sort(sortFn);
  };

  // Helper to get remaining budget for a category
  const getRemainingBudget = (categoryId) => {
    const goal = goals.find(g => g.category_id === categoryId);
    if (!goal) return 0;
    return goal.annual_amount;
  };

  const getCategoryById = (id) => categories.find((c) => c.id === id);

  // Group goals by category group, separating N/A goals
  const goalsByGroup = {};
  const naGoals = []; // Separate array for N/A goals
  
  goals.forEach((goal) => {
    const cat = getCategoryById(goal.category_id);
    if (cat) {
      if (goal.period_type === "na") {
        // N/A goals go to separate section
        naGoals.push({ ...goal, category: cat });
      } else {
        const group = cat.group;
        if (!goalsByGroup[group]) goalsByGroup[group] = [];
        goalsByGroup[group].push({ ...goal, category: cat });
      }
    }
  });

  // Categories without goals
  const categoriesWithGoals = new Set(goals.map((g) => g.category_id));
  const categoriesWithoutGoals = categories.filter((c) => !categoriesWithGoals.has(c.id));

  // Calculate group totals
  const getGroupTotal = (group) => {
    const groupGoals = goalsByGroup[group] || [];
    return groupGoals.reduce((sum, g) => sum + (g.annual_amount || 0), 0);
  };

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
            Budget Goals
          </h1>
          <p className="text-slate-500 mt-1">
            Set your annual spending targets for {year}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={exportToCSV}
            disabled={goals.length === 0}
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>

          <Button 
            variant={showYoYComparison ? "default" : "outline"}
            className={`gap-2 ${showYoYComparison ? "bg-slate-700 hover:bg-slate-600" : ""}`}
            onClick={() => setShowYoYComparison(!showYoYComparison)}
            data-testid="yoy-comparison-btn"
          >
            <TrendingUp className="h-4 w-4" />
            {showYoYComparison ? "Hide" : "Show"} {year - 1} Comparison
          </Button>

          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={() => setShowHistory(!showHistory)}
            data-testid="show-history-btn"
          >
            <History className="h-4 w-4" />
            {showHistory ? "Hide" : "Show"} History
          </Button>

          <Button 
            variant="outline" 
            className="gap-2" 
            onClick={handleOpenBatchDialog}
            data-testid="batch-goals-btn"
          >
            <Table2 className="h-4 w-4" />
            Batch Set Goals
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            {/* Only show Add Goal button if there are categories without goals */}
            {categoriesWithoutGoals.length > 0 && (
              <DialogTrigger asChild>
                <Button className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="add-goal-btn">
                  <Plus className="h-4 w-4" />
                  Add Goal
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {editingGoal ? "Edit Budget Goal" : "Add Budget Goal"}
                </DialogTitle>
                <DialogDescription>
                  {getSelectedCategory() 
                    ? `Setting ${getSelectedCategory().group === "Income" ? "income target" : "spending limit"} for ${getSelectedCategory().name}`
                    : "Select a category and enter your budget target"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formCategoryId} onValueChange={setFormCategoryId}>
                    <SelectTrigger data-testid="goal-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.emoji && `${cat.emoji} `}{cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formAmount}
                      onChange={(e) => setFormAmount(e.target.value)}
                      disabled={formPeriodType === "na"}
                      data-testid="goal-amount-input"
                    />
                    {/* % of income calculator - visually subordinate */}
                    {getGroupTotal("Income") > 0 && formPeriodType !== "na" && (
                      <div className="pt-1">
                        {!showPercentCalc ? (
                          <button
                            type="button"
                            onClick={() => setShowPercentCalc(true)}
                            className="text-xs text-slate-400 hover:text-slate-600"
                          >
                            Calculate from % of income
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              max="100"
                              placeholder="%"
                              value={percentOfIncome}
                              onChange={(e) => setPercentOfIncome(e.target.value)}
                              className="h-7 w-16 text-xs"
                            />
                            <span className="text-xs text-slate-500">% of {formatCurrency(getGroupTotal("Income"))}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs px-2"
                              onClick={calculateFromPercent}
                            >
                              Apply
                            </Button>
                            <button
                              type="button"
                              onClick={() => { setShowPercentCalc(false); setPercentOfIncome(""); }}
                              className="text-xs text-slate-400 hover:text-slate-600"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Period</Label>
                    <Select value={formPeriodType} onValueChange={(v) => {
                      setFormPeriodType(v);
                      if (v === "na") setFormAmount("0");
                    }}>
                      <SelectTrigger data-testid="goal-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="na">N/A (Don't track)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formAmount && formPeriodType && formPeriodType !== "na" && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    {(() => {
                      const newAmount = Math.abs(parseFloat(formAmount));
                      const hasChanged = editingGoal && !isNaN(newAmount) && (
                        newAmount !== editingGoal.amount || formPeriodType !== editingGoal.period_type
                      );
                      
                      // Calculate values
                      const newWeekly = formPeriodType === "weekly" ? newAmount :
                        formPeriodType === "monthly" ? newAmount * 12 / 52 : newAmount / 52;
                      const newMonthly = formPeriodType === "monthly" ? newAmount :
                        formPeriodType === "weekly" ? newAmount * 52 / 12 : newAmount / 12;
                      const newAnnual = formPeriodType === "annual" ? newAmount :
                        formPeriodType === "monthly" ? newAmount * 12 : newAmount * 52;
                      
                      if (editingGoal && hasChanged) {
                        // Show "from → to" format when value has changed
                        const oldWeekly = editingGoal.period_type === "weekly" ? editingGoal.amount :
                          editingGoal.period_type === "monthly" ? editingGoal.amount * 12 / 52 : editingGoal.amount / 52;
                        const oldMonthly = editingGoal.period_type === "monthly" ? editingGoal.amount :
                          editingGoal.period_type === "weekly" ? editingGoal.amount * 52 / 12 : editingGoal.amount / 12;
                        
                        return (
                          <>
                            <p className="font-medium text-slate-700 mb-1">Budget change:</p>
                            <div className="grid grid-cols-3 gap-2 text-slate-600 font-mono text-xs">
                              <div>
                                <span className="text-slate-400">Weekly:</span><br />
                                <span className="text-slate-400">{formatCurrency(oldWeekly)}</span>
                                <span className="text-slate-500"> → </span>
                                <span className="text-slate-900">{formatCurrency(newWeekly)}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">Monthly:</span><br />
                                <span className="text-slate-400">{formatCurrency(oldMonthly)}</span>
                                <span className="text-slate-500"> → </span>
                                <span className="text-slate-900">{formatCurrency(newMonthly)}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">Annual:</span><br />
                                <span className="text-slate-400">{formatCurrency(editingGoal.annual_amount)}</span>
                                <span className="text-slate-500"> → </span>
                                <span className="text-slate-900">{formatCurrency(newAnnual)}</span>
                              </div>
                            </div>
                          </>
                        );
                      } else {
                        // Show current values without arrow (for new goals or unchanged edits)
                        return (
                          <>
                            <p className="font-medium text-slate-700 mb-1">
                              {editingGoal ? "Current budget:" : "Normalized amounts:"}
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-slate-600 font-mono text-xs">
                              <div>
                                <span className="text-slate-400">Weekly:</span><br />
                                <span className="text-slate-900">{formatCurrency(newWeekly)}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">Monthly:</span><br />
                                <span className="text-slate-900">{formatCurrency(newMonthly)}</span>
                              </div>
                              <div>
                                <span className="text-slate-400">Annual:</span><br />
                                <span className="text-slate-900">{formatCurrency(newAnnual)}</span>
                              </div>
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>
                )}

                {formPeriodType === "na" && (
                  <div className="bg-slate-100 rounded-lg p-3 text-sm text-slate-600">
                    <p>This category will be marked as <strong>N/A</strong> and excluded from budget tracking.</p>
                    <p className="text-xs text-slate-500 mt-1">Use this for pass-through accounts or categories you don't need to monitor.</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    placeholder="e.g., Based on last year + 5%"
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    data-testid="goal-notes-input"
                  />
                </div>

                {/* Effective Date - subtle, expandable */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500">Effective from</Label>
                    <span className="text-xs text-slate-400">
                      {formEffectiveDate === `${year}-01-01` ? '(default: start of year)' : '(custom date)'}
                    </span>
                  </div>
                  <Input
                    type="date"
                    value={formEffectiveDate}
                    onChange={(e) => setFormEffectiveDate(e.target.value)}
                    className="text-sm h-9"
                    data-testid="goal-effective-date-input"
                  />
                </div>

                {/* Sign Warning */}
                {signWarning && (
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <p className="font-medium mb-2">{signWarning.message}</p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          size="sm"
                          onClick={fixSignAndSubmit}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          Yes, use positive
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={confirmWithWarning}
                        >
                          No, keep negative
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setSignWarning(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Zero-Sum Reallocation UI */}
                {showReallocation && budgetChange && (
                  <div className="border-2 border-sky-200 bg-sky-50 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-sky-800">
                      <ArrowRight className="h-5 w-5" />
                      <div>
                        <p className="font-semibold">
                          {budgetChange.direction === "increase" 
                            ? `Adding ${formatCurrency(budgetChange.proratedAmount)} for rest of ${year}`
                            : `Removing ${formatCurrency(budgetChange.proratedAmount)} for rest of ${year}`}
                        </p>
                        <p className="text-xs text-sky-600">
                          Full year: {formatCurrency(budgetChange.amount)} • {budgetChange.remainingDays} days remaining ({(budgetChange.proratedFactor * 100).toFixed(0)}% of year)
                        </p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-sky-700">
                      {budgetChange.direction === "increase"
                        ? "Where should this money come from?"
                        : "Where should this money go?"}
                    </p>

                    <div className="space-y-2">
                      {/* Other categories as source/destination */}
                      <Select value={reallocationSource} onValueChange={setReallocationSource}>
                        <SelectTrigger data-testid="reallocation-source-select">
                          <SelectValue placeholder="Select a source..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="savings">
                            <span className="flex items-center gap-2">
                              <PiggyBank className="h-4 w-4" />
                              {budgetChange.direction === "increase" ? "Savings (reduce savings goal)" : "Savings (increase savings goal)"}
                            </span>
                          </SelectItem>
                          <SelectItem value="unplanned_income">
                            <span className="flex items-center gap-2">
                              <Gift className="h-4 w-4" />
                              Unplanned Income (gift, windfall)
                            </span>
                          </SelectItem>
                          {goals
                            .filter(g => g.category_id !== formCategoryId && g.annual_amount > 0)
                            .map(g => {
                              const cat = getCategoryById(g.category_id);
                              // Calculate AVAILABLE budget (budget - YTD spent)
                              const availableBudget = getAvailableBudget(g.category_id);
                              // Calculate what would remain after reallocation
                              const afterReallocation = budgetChange.direction === "increase" 
                                ? availableBudget - budgetChange.proratedAmount
                                : availableBudget + budgetChange.proratedAmount;
                              const isInsufficient = budgetChange.direction === "increase" && afterReallocation < 0;
                              return (
                                <SelectItem 
                                  key={g.category_id} 
                                  value={g.category_id}
                                >
                                  <div className={`flex flex-col ${isInsufficient ? "opacity-60" : ""}`}>
                                    <span className="flex items-center gap-1">
                                      {cat?.emoji && <span>{cat.emoji}</span>}
                                      <span className={isInsufficient ? "text-slate-400" : ""}>{cat?.name}</span>
                                      {isInsufficient && (
                                        <span className="text-rose-500 text-xs font-medium ml-1 bg-rose-50 px-1.5 py-0.5 rounded">
                                          insufficient
                                        </span>
                                      )}
                                    </span>
                                    <span className="text-xs text-slate-400 mt-0.5">
                                      {formatCurrency(availableBudget)} available → {formatCurrency(afterReallocation)} after
                                    </span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs text-sky-700">Note (optional)</Label>
                      <Input
                        placeholder="e.g., Price increases, one-time adjustment"
                        value={reallocationNote}
                        onChange={(e) => setReallocationNote(e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        onClick={() => submitWithReallocation(reallocationSource)}
                        disabled={!reallocationSource}
                        className="bg-sky-600 hover:bg-sky-700"
                        data-testid="confirm-reallocation-btn"
                      >
                        Confirm Reallocation
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowReallocation(false);
                          setBudgetChange(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {!showReallocation && (
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-goal-btn">
                      {editingGoal ? "Update" : "Create"} Goal
                    </Button>
                  </div>
                )}
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Net Income / Savings Goal - PROMINENT */}
      {goals.length > 0 && (
        <TooltipProvider>
          <Card className="border-2 border-slate-900 bg-slate-900 text-white">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="md:col-span-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-slate-400 text-sm font-medium flex items-center gap-1 cursor-help">
                        Income Goal
                        <Info className="h-3 w-3 opacity-50" />
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">Take-home pay: Gross pay minus taxes, 401k, and other withholdings</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className="text-2xl font-bold font-mono mt-1 text-emerald-400">
                    {formatCurrency(getGroupTotal("Income"))}
                  </p>
                </div>
                <div className="md:col-span-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-slate-400 text-sm font-medium flex items-center gap-1 cursor-help">
                        Expense Goal
                        <Info className="h-3 w-3 opacity-50" />
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">Sum of Bills + Needs + Wants category groups. This is your total planned spending.</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className="text-2xl font-bold font-mono mt-1 text-rose-400">
                    {formatCurrency(getGroupTotal("Bills") + getGroupTotal("Needs") + getGroupTotal("Wants"))}
                  </p>
                </div>
                <div className="md:col-span-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-slate-400 text-sm font-medium flex items-center gap-1 cursor-help">
                        Budget Savings
                        <Info className="h-3 w-3 opacity-50" />
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">Income Goal − Expense Goal = money left over from your take-home pay (before 401k)</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className={`text-2xl font-bold font-mono mt-1 ${
                    getGroupTotal("Income") - (getGroupTotal("Bills") + getGroupTotal("Needs") + getGroupTotal("Wants")) >= 0
                      ? "text-emerald-400"
                      : "text-rose-400"
                  }`}>
                    {formatCurrency(getGroupTotal("Income") - (getGroupTotal("Bills") + getGroupTotal("Needs") + getGroupTotal("Wants")))}
                  </p>
                </div>
                <div className="md:col-span-1">
                  <p className="text-slate-400 text-sm font-medium">401k Goal</p>
                  <p className="text-2xl font-bold font-mono mt-1 text-sky-400">
                    {formatCurrency(retirementSummary?.total_annual_goal || 0)}
                  </p>
                </div>
                <div className="md:col-span-1 md:text-right">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-slate-400 text-sm font-medium flex items-center gap-1 justify-end cursor-help">
                        Total Savings Goal
                        <Info className="h-3 w-3 opacity-50" />
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">Budget Savings + 401k Goal = your total expected savings including retirement contributions</p>
                    </TooltipContent>
                  </Tooltip>
                  <p className="text-3xl font-bold font-mono mt-1 text-emerald-400">
                    {formatCurrency(
                      (getGroupTotal("Income") - (getGroupTotal("Bills") + getGroupTotal("Needs") + getGroupTotal("Wants"))) +
                      (retirementSummary?.total_annual_goal || 0)
                    )}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">
                    {(((getGroupTotal("Income") - (getGroupTotal("Bills") + getGroupTotal("Needs") + getGroupTotal("Wants"))) + (retirementSummary?.total_annual_goal || 0)) / ((getGroupTotal("Income") || 1) + (retirementSummary?.total_annual_goal || 0)) * 100).toFixed(1)}% total savings rate
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TooltipProvider>
      )}

      {/* Unset Goals - AT THE TOP */}
      {categories.length > 0 && (
        <Card className={`border-slate-200 ${categoriesWithoutGoals.length > 0 ? 'border-dashed border-amber-300 bg-amber-50/30' : 'bg-emerald-50/30 border-emerald-200'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              {categoriesWithoutGoals.length > 0 ? (
                <>
                  <Target className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                  <span className="text-amber-700">Categories without goals</span>
                  <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                    {categoriesWithoutGoals.length} remaining
                  </Badge>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
                  <span className="text-emerald-700">All categories have goals set</span>
                </>
              )}
            </CardTitle>
            {categoriesWithoutGoals.length > 0 && (
              <CardDescription>
                Click a category to set its {year} budget
              </CardDescription>
            )}
          </CardHeader>
          {categoriesWithoutGoals.length > 0 && (
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {categoriesWithoutGoals.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setFormCategoryId(cat.id);
                      setDialogOpen(true);
                    }}
                    className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 rounded-full text-sm text-slate-600 transition-colors"
                    data-testid={`unset-category-${cat.id}`}
                  >
                    {cat.emoji && `${cat.emoji} `}{cat.name}
                  </button>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Goals by Group - Collapsible */}
      {["Income", "Bills", "Needs", "Wants"].map((group) => {
        const groupGoals = goalsByGroup[group] || [];
        if (groupGoals.length === 0) return null;
        
        const groupTotal = getGroupTotal(group);
        const isCollapsed = collapsedGroups[group];

        return (
          <Collapsible key={group} open={!isCollapsed} onOpenChange={() => toggleGroup(group)}>
            <Card className="border-slate-200">
              <CollapsibleTrigger asChild>
                <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                  <CardTitle className="font-['Outfit'] text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                      )}
                      {group}
                      <Badge variant="secondary" className="ml-2">
                        {groupGoals.length} {groupGoals.length === 1 ? 'goal' : 'goals'}
                      </Badge>
                    </div>
                    <span className="font-mono text-base font-semibold text-slate-700">
                      {formatCurrency(groupTotal)}/yr
                    </span>
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-3">
                    {groupGoals.map((goal) => {
                      const prevYearGoal = showYoYComparison ? getPreviousYearGoal(goal.category_id) : null;
                      const prevYearActual = showYoYComparison ? getPreviousYearActual(goal.category_id) : 0;
                      
                      return (
                        <div
                          key={goal.id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="font-medium text-slate-900">
                                {goal.category?.emoji && `${goal.category.emoji} `}
                                {goal.category?.name}
                              </p>
                              <p className="text-sm text-slate-500">
                                {goal.period_type === "na" ? (
                                  <span className="text-slate-400 italic">N/A - Not tracked</span>
                                ) : (
                                  <>
                                    {formatCurrency(goal.amount)} / {goal.period_type}
                                    {goal.notes && <span className="ml-2 text-slate-400">• {goal.notes}</span>}
                                  </>
                                )}
                              </p>
                              {/* YoY Comparison */}
                              {showYoYComparison && goal.period_type !== "na" && (
                                <div className="flex items-center gap-3 mt-1 text-xs">
                                  <span className="text-slate-400">
                                    {year - 1} Goal: {prevYearGoal ? formatCurrency(prevYearGoal.annual_amount) : "—"}
                                  </span>
                                  <span className="text-slate-400">
                                    {year - 1} Actual: {prevYearActual > 0 ? formatCurrency(prevYearActual) : "—"}
                                  </span>
                                  {prevYearGoal && (
                                    <span className={`flex items-center gap-0.5 ${
                                      goal.annual_amount > prevYearGoal.annual_amount 
                                        ? (goal.category?.group === "Income" ? "text-emerald-600" : "text-rose-600")
                                        : goal.annual_amount < prevYearGoal.annual_amount
                                        ? (goal.category?.group === "Income" ? "text-rose-600" : "text-emerald-600")
                                        : "text-slate-400"
                                    }`}>
                                      {goal.annual_amount > prevYearGoal.annual_amount ? (
                                        <TrendingUp className="h-3 w-3" />
                                      ) : goal.annual_amount < prevYearGoal.annual_amount ? (
                                        <TrendingDown className="h-3 w-3" />
                                      ) : null}
                                      {goal.annual_amount !== prevYearGoal.annual_amount && (
                                        <span>
                                          {((goal.annual_amount - prevYearGoal.annual_amount) / prevYearGoal.annual_amount * 100).toFixed(0)}%
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              {goal.period_type === "na" ? (
                                <p className="font-mono text-slate-400 italic">N/A</p>
                              ) : (
                                <>
                                  <p className="font-mono font-semibold text-slate-900">
                                    {formatCurrency(goal.annual_amount)}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    from {goal.effective_date ? new Date(goal.effective_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Jan 1'}
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleEdit(goal); }}
                                data-testid={`edit-goal-${goal.id}`}
                              >
                                <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`delete-goal-${goal.id}`}
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}

      {/* Not Tracked Section - for N/A goals */}
      {naGoals.length > 0 && (
        <Collapsible 
          open={!collapsedGroups["NotTracked"]} 
          onOpenChange={() => toggleGroup("NotTracked")}
          defaultOpen={false}
        >
          <Card className="border-slate-200">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {collapsedGroups["NotTracked"] !== false ? (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                    <CardTitle className="font-['Outfit'] text-lg text-slate-500">
                      Not Tracked
                    </CardTitle>
                    <Badge variant="secondary" className="bg-slate-100 text-slate-500">
                      {naGoals.length} {naGoals.length === 1 ? 'category' : 'categories'}
                    </Badge>
                  </div>
                  <span className="text-slate-400 italic text-sm">N/A</span>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {naGoals.map((goal) => (
                    <div
                      key={goal.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium text-slate-500">
                            {goal.category?.emoji && `${goal.category.emoji} `}
                            {goal.category?.name}
                          </p>
                          <p className="text-sm text-slate-400 italic">
                            N/A - Not tracked
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleEdit(goal); }}
                          data-testid={`edit-goal-${goal.id}`}
                        >
                          <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-goal-${goal.id}`}
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {goals.length === 0 && categories.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-12 w-12 text-slate-300 mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">No categories yet</h3>
            <p className="text-slate-500 mb-4">Import your YNAB data or add categories first</p>
          </CardContent>
        </Card>
      )}

      {/* Change History Panel */}
      {showHistory && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <History className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
              Budget Change History
              <Badge variant="secondary" className="ml-2">
                {budgetChanges.filter(c => c.reallocation_type !== 'initial').length} changes
              </Badge>
            </CardTitle>
            <CardDescription>
              Audit trail of all budget modifications for {year}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {budgetChanges.filter(c => c.reallocation_type !== 'initial').length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Date</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Category</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">Change</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Source / Destination</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetChanges
                      .filter(c => c.reallocation_type !== 'initial')
                      .map((change) => (
                        <tr key={change.id} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-600">
                            {new Date(change.change_date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </td>
                          <td className="py-2 px-3 font-medium text-slate-900">
                            {change.category_name}
                          </td>
                          <td className={`py-2 px-3 text-right font-mono ${
                            change.change_annual > 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {change.change_annual > 0 ? '+' : ''}{formatCurrency(change.change_annual)}
                          </td>
                          <td className="py-2 px-3 text-slate-600">
                            {change.reallocation_type === 'from_savings' && '← Savings'}
                            {change.reallocation_type === 'to_savings' && '→ Savings'}
                            {change.reallocation_type === 'from_unplanned' && '← Unplanned Income'}
                            {change.reallocation_type === 'from_category' && change.reallocation_target_name && `← ${change.reallocation_target_name}`}
                            {change.reallocation_type === 'to_category' && change.reallocation_target_name && `→ ${change.reallocation_target_name}`}
                          </td>
                          <td className="py-2 px-3 text-slate-500 text-xs">
                            {change.note || '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">
                No budget changes yet. Changes will appear here when you edit goals.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Batch Goals Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Batch Set Goals</DialogTitle>
            <DialogDescription>
              Set budget goals for multiple categories at once. Leave amounts blank to delete existing goals.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-all-categories"
                  checked={batchShowAll}
                  onCheckedChange={setBatchShowAll}
                />
                <Label htmlFor="show-all-categories" className="text-sm">
                  Show all categories (including those with existing goals)
                </Label>
              </div>
              <Badge variant="secondary">
                {getBatchCategories().length} categories
              </Badge>
            </div>

            <div className="border rounded-lg">
              <div className="grid grid-cols-12 gap-2 p-3 bg-slate-50 border-b text-xs font-medium text-slate-600">
                <div className="col-span-4">Category</div>
                <div className="col-span-3">Amount</div>
                <div className="col-span-2">Period</div>
                <div className="col-span-3">Annual Equivalent</div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {(() => {
                  const sortedCategories = getBatchCategories();
                  let currentGroup = null;
                  
                  return sortedCategories.map((category) => {
                    const goalData = batchGoals[category.id] || {};
                    const amount = parseFloat(goalData.amount || "0");
                    const periodType = goalData.periodType || "annual";
                    const hasExistingGoal = !!goalData.originalGoalId;
                    
                    // Calculate annual equivalent
                    let annualEquivalent = 0;
                    if (!isNaN(amount) && amount > 0) {
                      if (periodType === "weekly") annualEquivalent = amount * 52;
                      else if (periodType === "monthly") annualEquivalent = amount * 12;
                      else annualEquivalent = amount;
                    }
                    
                    // Orange background for unset goals when showing all
                    const rowClassName = batchShowAll && !hasExistingGoal
                      ? "grid grid-cols-12 gap-2 p-3 border-b border-amber-300 bg-amber-100 hover:bg-amber-200"
                      : "grid grid-cols-12 gap-2 p-3 border-b border-slate-100 hover:bg-slate-50";
                    
                    // Check if we need a group header
                    const showGroupHeader = category.group !== currentGroup;
                    if (showGroupHeader) currentGroup = category.group;
                    
                    return (
                      <div key={category.id}>
                        {showGroupHeader && (
                          <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 uppercase tracking-wide border-b">
                            {category.group}
                          </div>
                        )}
                        <div className={rowClassName}>
                          <div className="col-span-4 flex items-center">
                            <span className="text-sm">
                              {category.emoji && `${category.emoji} `}
                              {category.name}
                            </span>
                            {hasExistingGoal && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                has goal
                              </Badge>
                            )}
                          </div>
                      
                      <div className="col-span-3">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={goalData.amount || ""}
                          onChange={(e) => updateBatchGoal(category.id, "amount", e.target.value)}
                          className="h-8 text-sm"
                          disabled={goalData.periodType === "na"}
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Select 
                          value={goalData.periodType || "annual"} 
                          onValueChange={(v) => {
                            updateBatchGoal(category.id, "periodType", v);
                            if (v === "na") updateBatchGoal(category.id, "amount", "0");
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                            <SelectItem value="na">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="col-span-3 flex items-center">
                        <span className="text-sm font-mono text-slate-600">
                          {goalData.periodType === "na" ? (
                            <span className="italic text-slate-400">N/A</span>
                          ) : (
                            formatCurrency(annualEquivalent)
                          )}
                        </span>
                      </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBatchSave} 
              disabled={batchSaving}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {batchSaving ? "Saving..." : "Save All Goals"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
